import React, { useEffect, useState } from "react";
import {
  IoIosArrowRoundBack,
  IoIosArrowRoundForward,
  IoIosPersonAdd,
} from "react-icons/io";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/stateContext";
import SortableTableHeader from "../../components/Table/SortableTableHeader";


const CreditRepaymentDetails = () => {
  const { requestWithAuth } = useAuth();
  const {credit_id} = useParams();
  const { activeSession } = useGlobalContext();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [updating, setUpdating] = useState(false)
  const [approving, setApproving] = useState(false)

  const fetchSales = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      let url = `/inventory/credit-sale/${credit_id}/items?page=${current_page}&pageSize=${page_size}&search=${search_query}`;
      const sortParam = sortBy ? `&sort_by=${sortBy}&sort_order=${sortOrder}` : "";
      url += sortParam;
      const data = await requestWithAuth(url, "GET");
      setSales(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterSalesList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1); 
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of pages to show around the current page
    const totalPagesToShow = 5; // Total pages to show at start and end

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    // Always show the first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    // Show the numbers around the current page
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number) => (
      <button
        key={number}
        onClick={() => number !== "..." && handlePageClick(number)}
        className={
          number === currentPage
            ? "border border-[#979797] bg-gradient-to-b from-white to-[#dcdcdc] py-[0.5rem] px-[1rem]"
            : "py-[0.5rem] px-[1rem]"
        }
      >
        {number}
      </button>
    ));
  };

  const repayCredit = (credit) => {
    Swal.fire({
      icon:"question",
      title: `Do you want to proceed to record payment?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        setUpdating(true);
        const body = JSON.stringify({
            branch: activeSession?.branch?.id,
            session: activeSession?.session,
            sale_item:credit?.id,
            sale:credit?.sale,
            amount_paid:credit?.total_selling_price
        })
        try {
          const data = await requestWithAuth(
            `/inventory/credit-payment/`,
            "POST",body, {
                "Content-Type": "application/json",
              }
          );
          Swal.fire({
            icon:"success",
            title: "Payment Recorded!",
            text: "Credit payment recorded successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
            if (error.response.data.error) {
                for (const property in error.response.data.error) {
                  toast.error(
                    `${property} error: ${error.response.data.error[property]}`
                  );
                }
              }
              if (error.response.status === 500) {
                toast.error("Something went wrong!");
              }
          
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const returnSale = (sale_id, item_id) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to return this item?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        setApproving(true);
        const body=JSON.stringify({
            return_type:"partial",
            item_id:item_id
        })
        try {
          const data = await requestWithAuth(
            `/inventory/return-sale/${sale_id}/`,
            "POST", body,{
                'Content-Type':"application/json"
            }
          );
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Sale returned Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
          console.error("Error updating drug:", error);
        } finally {
          setApproving(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  useEffect(() => {
    fetchSales(pageSize, search, currentPage);
  }, [search, currentPage, pageSize, sortBy, sortOrder]);

  return (
    <div className="p-[20px]">
        
      <div className="py-[32px] flex items-center justify-between ">
        <div className="flex items-center gap-3">
          <IoIosPersonAdd className="text-[19px]" />
          <h4 className="text-[19px]">
            <b>Credit</b> - Repayment
          </h4>
        </div>
        
      </div>
      <div className="border border-[#ddd] bg-white">
        <div className="p-[20px]">
          <h5 className="my-[2px] text-[17px]">Unsettled Credit Details</h5>
        </div>
        <div className="p-[20px]">
          <div className="p-[20px] border border-[#ddd]">
            <div className="pt-[20px] flex justify-between mb-[20px]">
              <div className=" flex items-center gap-5">
                <div className="flex">
                  <div className="bg-[#fcfcfc] border border-[#ddd] py-[7px] px-[12px] cursor-pointer">
                    CSV
                  </div>
                  <div className="bg-[#fcfcfc] border border-[#ddd] py-[7px] px-[12px] cursor-pointer">
                    PDF
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <b>Show:</b>
                  <select
                    name=""
                    id=""
                    onChange={(e) => setPageSize(e.target.value)}
                    className="border border-[#ddd] rounded-[3px] py-[7px] px-[12px] text-[13px]"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <b>Filter:</b>
                <input
                  type="text"
                  placeholder="Type to filter..."
                  className="outline-none border border-[#ddd] rounded-[3px] h-[36px] w-full text-[#333] text-[13px] px-[12px] py-[7px]"
                  onChange={(e) => filterSalesList(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-auto ">
              {!loading ? (
                <table className="border-collapse w-full">
                  <thead>
                    <tr className="text-left">
                      <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                        Item Description
                      </th>
                      <SortableTableHeader
                        label="Quantity"
                        fieldKey="quantity"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        thClassName="border-b border-t border-[#bbb]"
                      />
                      <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                        Unit Price
                      </th>
                      <SortableTableHeader
                        label="Total Cost"
                        fieldKey="total_selling_price"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        thClassName="border-b border-t border-[#bbb]"
                      />                      
                      <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales?.map((sale, index) => {
                    if(sale?.payment_status != "paid"){
                        return(
                          <tr key={index} className="text-left">
                            <td className="border-b border-[#bbb] py-[12px] px-[20px]">
                              {sale?.drug}
                            </td>
                            <td className="border-b border-[#bbb] py-[12px] px-[20px]">
                              {sale?.quantity}
                            </td>
                            <td className="border-b border-[#bbb] py-[12px] px-[20px]">
                              {sale?.unit_cost}
                            </td>
                            <td className="border-b border-[#bbb] py-[12px] px-[20px]">
                              {sale?.total_selling_price}
                            </td>
                          
                          {!updating ? (
                                <td className="border-b border-[#bbb] py-[12px] px-[20px] flex gap-1">          
                                {
                                  sale?.credit_type == "Customer" && (
                                    <div
                                        onClick={()=>repayCredit(sale)}
                                        className="cursor-pointer hover:bg-[#008fa1] bg-[#00BCD4] text-[10px] pt-[3px] px-[5px] pb-[1px] text-white"
                                    >
                                        PAY CREDIT
                                    </div>
                                  )
                                }
                                {
                                  activeSession?.user?.role != "Cashier" && (
                                  
                                    <div
                                        onClick={()=>returnSale(sale.sale, sale.id)}
                                        className="hover:bg-[#a10300] bg-[#d40b00] text-[10px] cursor-pointer pt-[3px] px-[5px] pb-[1px] text-white"
                                    >
                                        RETURN
                                    </div>
                                  )
                                }
                                </td>
                          ):(
                            <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                              <RotatingLines
                                height="20"
                                width="20"
                                strokeColor="black"
                              />{" "}
                              
                            </td>
                          )}
                          </tr>
                        )
                    }
                    }       
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="flex items-center justify-center">
                  <RotatingLines
                    visible={true}
                    height="36"
                    width="36"
                    color="black"
                    strokeWidth="5"
                    animationDuration="0.75"
                    ariaLabel="rotating-lines-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                </div>
              )}
              {sales?.length <= 0 && !loading && (
                <p className="text-center py-5">No data availabe in table</p>
              )}
              <div className="flex justify-between pt-[20px]">
                <p className="text-[12px] text-[#888]">
                  Showing {currentPage} of {totalPages} pages
                </p>
                <div className="flex items-center gap-2">
                  <IoIosArrowRoundBack className="text-[25px]" onClick={()=>handlePreviousPage()} />
                  {renderPageNumbers()}
                  <IoIosArrowRoundForward className="text-[25px]" onClick={()=>handleNextPage()} />
                </div>
              </div>
            </div>
          </div>
        </div>
        .
      </div>
    </div>
  );
};

export default CreditRepaymentDetails;
